'use client';

import { useCallback, useEffect, useRef, useState } from 'react';
import { type VoiceWsMessage, useVoiceWs } from './use-voice-ws';

/**
 * V2.11 (2026-05-28j) — voice chat loop, direction-C: mic + Doubao
 * session stay live across turn commits. Turn boundaries are tracked
 * by the *length* of the cumulative Doubao transcript at commit time;
 * the next partial's display text is `fullText.slice(committedLen)`.
 *
 * Why: previously we tore down + restarted the Doubao session on
 * every fireFinal. The combined audio_end → asr_task wait → audio_start
 * → Doubao connect ack could take 5-10s and races the LLM round-trip.
 * Users saw 「正在聆听」, started talking, but the mic was actually
 * dead until the restart finished — looked like the system stopped
 * responding on round 2/3.
 *
 * New model:
 *   enterVoice: one `startRecording` (= one Doubao session). Reset the
 *               turn pointers.
 *   partial:    `display = fullText.slice(committedLen)`. Only the
 *               current turn's portion drives the bubble, silence
 *               timer, and barge-in detection.
 *   fireFinal:  setPhase('thinking') → record committedLen = current
 *               fullText.length → kick LLM. Mic + Doubao keep running
 *               under the hood; new partials accumulate into the next
 *               turn's display from offset 0.
 *   barge-in:   thinking + display non-empty → abort LLM → back to
 *               listening.
 *   exitVoice / asr_error: one stopRecording / disconnect, then a
 *               fresh enterVoice when re-engaged.
 *
 * Caveat: a long voice session means a long Doubao transcript. If
 * Doubao tears down the session (server idle or transcript-length
 * limit), we'll see asr_error and run one full reconnect — that path
 * resets the turn pointers because it's a brand-new Doubao session.
 */

/**
 * V3.4 (2026-06-03) — phase 'warmup' = mic is open but hermes hasn't
 * acked asr_started yet. Doubao session takes ~500ms–3s to come up
 * from CF Singapore; before the ack arrives, hermes drops any PCM
 * frames we send. The client buffers PCM in the meantime (see
 * use-voice-ws.ts asrReadyRef + pcmBufferRef), but until the flush we
 * also need to tell the user 「准备中」 so they don't speak yet.
 */
export type VoicePhase = 'off' | 'idle' | 'warmup' | 'listening' | 'thinking';

/** Silence (no new partial) duration that means the user is done.
 *  2026-06-23 — 10s → 5s. With the soft-pause refactor, Doubao no
 *  longer dies between turns so 「再次自动出现确认卡」 is harmless.
 *  Leon's earlier 10s tuning was to fight cross-turn race; gone now.
 *  Also: silence timer now fires regardless of manualMode (was
 *  suppressed after 「继续讲」 — but 5s is longer than any natural
 *  pause, so it's safe + matches user expectation). */
const SILENCE_HOLD_MS = 5000;
/** No-activity timeout: drop to 'idle' so the UI surfaces 「点击继续」.
 *  2026-06-15 — bumped 12s → 25s so the bar doesn't auto-pause while the
 *  user is reading / editing the confirm card after a Doubao final. */
const IDLE_TIMEOUT_MS = 25000;
/** 2026-06-23 — session-level hard cap. Doubao streaming WS session
 *  TTL is undocumented but observed to degrade past ~5 min. Cap at
 *  3 min and force a clean tear-down + 「继续」 prompt, so the user
 *  hits a predictable boundary instead of a silent ASR-died failure.
 *  Trade-off: long answers get interrupted, but the alternative
 *  (random mid-sentence death) is worse for trust. */
const SESSION_MAX_MS = 180_000;

/** 2026-06-23 v7 — translate Doubao-internal asr_error strings into a single
 *  user-facing Chinese line. Returns `null` when the error is a transient
 *  recovery signal that hermes will handle on its own (Codex round 19
 *  caught the raw "Timeout waiting next packet ... session has ended"
 *  bleeding into the banner). Anything Doubao-internal is collapsed to the
 *  same neutral message so the user never sees the engine's words. */
function translateAsrError(raw: string): string | null {
  const s = raw || '';
  // Doubao timeout-then-auto-reopen — hermes recreates the session
  // server-side. Don't show anything; the next utterance will work.
  if (/Timeout waiting next packet|session has ended|waiting next packet timeout/i.test(s)) {
    return null;
  }
  if (/doubao_reopen_rate_limited/i.test(s)) {
    return '语音连接频繁断开，请稍候再试或刷新页面。';
  }
  if (/doubao_reopen_failed/i.test(s)) {
    return '语音引擎重连失败，请刷新页面再试。';
  }
  if (/asr_empty|no_speech/i.test(s)) {
    return null;
  }
  if (/网络|network/i.test(s)) {
    return '语音网络异常，请稍候再试。';
  }
  return '语音识别出错，请重新说一次。';
}

export function useVoiceChatLoop(opts: {
  onPartial: (text: string) => void;
  onFinal: (text: string, signal: AbortSignal) => Promise<void>;
  onError?: (msg: string) => void;
  onFatalError?: (msg: string) => void;
}) {
  const [phase, setPhase] = useState<VoicePhase>('off');
  // 2026-06-24 v13 — pure PTT (Leon clarified intent). Mic OFF by default,
  // user clicks 点击说话 to start; live captions stream while speaking;
  // 5s silence OR explicit 结束发送 click commits → stop mic → polish →
  // confirm card. The 单次/实时 toggle stays removed (v11), but the
  // underlying `realtimeRef` is pinned `false` because all the PTT-only
  // code paths (idle button, stop-on-commit, fresh session per click)
  // are still wired through that ref. Realtime branches in this hook
  // become dead code; v14+ can prune them.
  const [realtime, setRealtime] = useState(false);
  const phaseRef = useRef<VoicePhase>('off');
  const realtimeRef = useRef(false);
  // 2026-06-15 — manualMode disables the silence-auto-commit. When true,
  // silence timer no-ops; only commitNow() / pauseListening() / endTurn()
  // can fire the final. Used by 「继续讲」 so the user can speak multiple
  // sentences without being interrupted by the confirm card.
  const manualModeRef = useRef(false);
  // 2026-06-23 v7 — PTT endTurn watchdog. Declared up here so both
  // onMessage (asr_final / asr_error clear) and exitVoice can reach it
  // without a TDZ in their useCallback deps. See endTurn for the timer
  // semantics — 4s safety net after stopRecording.
  const endTurnWatchdogRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const clearEndTurnWatchdog = useCallback(() => {
    if (endTurnWatchdogRef.current) {
      clearTimeout(endTurnWatchdogRef.current);
      endTurnWatchdogRef.current = null;
    }
  }, []);
  useEffect(() => {
    phaseRef.current = phase;
  }, [phase]);
  useEffect(() => {
    realtimeRef.current = realtime;
  }, [realtime]);

  const setPhaseNow = useCallback((p: VoicePhase) => {
    phaseRef.current = p;
    setPhase(p);
  }, []);

  const onPartialRef = useRef(opts.onPartial);
  const onFinalRef = useRef(opts.onFinal);
  const onErrorRef = useRef(opts.onError);
  const onFatalErrorRef = useRef(opts.onFatalError);
  useEffect(() => {
    onPartialRef.current = opts.onPartial;
    onFinalRef.current = opts.onFinal;
    onErrorRef.current = opts.onError;
    onFatalErrorRef.current = opts.onFatalError;
  });

  // Doubao-session-cumulative text — full transcript from when the
  // current Doubao session started, including all previously-committed
  // turns.
  const latestFullTextRef = useRef<string>('');
  // Length of `latestFullTextRef.current` at the moment the most
  // recent turn was committed. The next turn's display text is
  // `fullText.slice(committedFullTextLenRef.current)`.
  const committedFullTextLenRef = useRef(0);

  // Display text for the *current* turn (already sliced).
  const latestPartialRef = useRef<string>('');
  // V3.4 (2026-06-02) — per-turn "longest-prefix-survives" buffer.
  // Doubao's per-partial `text` field is *supposed* to be the
  // session-cumulative transcript and grow monotonically, but in
  // practice after a silence + new utterance it can transiently
  // return JUST the new utterance (without the prefix we've already
  // shown). The naive `fullText.slice(committedLen)` then visibly
  // erases what the user already said, until a later partial
  // restores the full string. Leon reported this verbatim:
  //   「中间插入了新的对话，前面说的就被覆盖掉了」「最后又出现了前面说的」.
  //
  // mergeIntoTurnBuffer handles the three cases distinctly:
  //   (a) incoming extends/equals what we have → use incoming
  //       (normal monotonic growth or same-length correction)
  //   (b) incoming is shorter AND existing starts with incoming →
  //       Doubao tail-truncated; keep existing
  //   (c) incoming is shorter AND existing doesn't start with
  //       incoming → Doubao lost the prefix; CONCATENATE so the
  //       newly-spoken text gets appended instead of overwriting
  const turnDisplayBufferRef = useRef('');
  function mergeIntoTurnBuffer(incoming: string): string {
    const existing = turnDisplayBufferRef.current;
    if (!existing) {
      turnDisplayBufferRef.current = incoming;
      return incoming;
    }
    if (incoming.length >= existing.length) {
      turnDisplayBufferRef.current = incoming;
      return incoming;
    }
    if (existing.startsWith(incoming)) {
      // Doubao truncated tail — keep what we already showed.
      return existing;
    }
    // 2026-06-24 v23 — Codex R34-F2 root cause. The old "concat fresh
    // utterance" path triggered REPEATEDLY when Doubao dropped prefix
    // multiple times in one session, producing spam like
    // "一二线城市服务业 服务业 PPI PPI最近 服务业 PPI最近...". Each
    // shorter partial that didn't start with existing got concat'd again.
    //
    // Per-utterance Doubao mode (end_window_size=60000) means one PTT
    // session = one utterance, so prefix-loss across utterances doesn't
    // happen via that path. Within-utterance prefix loss is rare and
    // usually small — we'd rather lose 2-3 characters than risk the
    // duplication storm. Keep existing.
    return existing;
  }

  const pttCommitPendingRef = useRef(false);
  const silenceTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const idleTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const abortRef = useRef<AbortController | null>(null);

  // 2026-06-23 — session-level 3-minute timer. Replaces the per-turn
  // Doubao reconnect with a single capped streaming session. Tick
  // every 1s for the UI countdown; on zero, force a clean shutdown
  // (exitVoice) and surface `sessionExpired` so session-panel can
  // show 「3 分钟上限，点击继续」.
  const sessionStartAtRef = useRef<number | null>(null);
  const sessionTickRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const [remainingSec, setRemainingSec] = useState<number | null>(null);
  const [sessionExpired, setSessionExpired] = useState(false);
  const onSessionExpireRef = useRef<(() => void) | null>(null);
  const clearSessionTimer = useCallback(() => {
    if (sessionTickRef.current) {
      clearInterval(sessionTickRef.current);
      sessionTickRef.current = null;
    }
    sessionStartAtRef.current = null;
    setRemainingSec(null);
  }, []);
  // 2026-06-24 v22 — Codex R34-F3: 3-min session cap retired. The cap
  // was added when voice mode kept ONE long-lived Doubao session hot
  // across all turns; that model is gone (v13 pure PTT opens a fresh
  // session per 点击说话). With per-utterance sessions, accumulated
  // session time across multiple answers is meaningless — and the
  // 3-min banner was firing mid-training, blocking the user mid-flow.
  // Leon: "每答一题都是新 session, 这个 cap 不需要了".
  // The function is kept as a no-op so existing call sites compile,
  // but neither remainingSec nor sessionExpired ever flip true now.
  const startSessionTimer = useCallback(() => {
    clearSessionTimer();
    setSessionExpired(false);
  }, [clearSessionTimer]);

  const clearSilenceTimer = useCallback(() => {
    if (silenceTimerRef.current) {
      clearTimeout(silenceTimerRef.current);
      silenceTimerRef.current = null;
    }
  }, []);

  const clearIdleTimer = useCallback(() => {
    if (idleTimerRef.current) {
      clearTimeout(idleTimerRef.current);
      idleTimerRef.current = null;
    }
  }, []);

  const armIdleTimer = useCallback(() => {
    clearIdleTimer();
    idleTimerRef.current = setTimeout(() => {
      idleTimerRef.current = null;
      if (phaseRef.current !== 'listening') return;
      if (typeof console !== 'undefined') {
        console.warn('[voice v17] idle timeout — stepping back to idle');
      }
      setPhaseNow('idle');
    }, IDLE_TIMEOUT_MS);
  }, [clearIdleTimer, setPhaseNow]);

  /** Reset the turn pointers. Called whenever a fresh Doubao session
   *  is opened (enterVoice, startTurn for PTT, asr_error reconnect,
   *  realtime resume from idle). */
  const resetTurnPointers = useCallback(() => {
    latestFullTextRef.current = '';
    committedFullTextLenRef.current = 0;
    latestPartialRef.current = '';
    turnDisplayBufferRef.current = '';
  }, []);

  const fireFinalRef = useRef<(t: string) => void>(() => {});

  // CRO-108 (2026-06-17) — set to true while the AI's TTS is playing.
  // Declared up here so onMessage (a few hooks below) can check it from
  // its closure. Mutated by holdForTts / resumeFromTts further down.
  const ttsHoldRef = useRef(false);
  // 2026-06-18 — set if Doubao sent asr_final while ttsHoldRef was true.
  // asr_final on the realtime branch means Doubao closed its session;
  // outside TTS hold the onMessage handler does a full Doubao restart
  // (line 339-362), but that restart is gated on phase != off/idle and
  // skipped under TTS hold so we don't trigger a flurry of mic-on/off
  // while audio is playing. The flag lets resumeFromTts know the
  // session is dead and forces a full WS reconnect when it brings the
  // mic back. Without this the UI flips to 'listening' over a zombie
  // session and the user's next utterance gets nothing — exactly the
  // symptom Leon reported: 「下面显示还是在实时聆听，但是我再说话就收不到音」.
  // 2026-06-25 v25 Phase 4 — `doubaoSessionDeadRef` removed. It was only
  // set by the v9 mid-PTT auto-close recovery path that v25.2 deleted.
  // Per-turn architecture (v25) makes session lifecycle explicit so
  // there's no "is it dead?" question to answer — every startTurn opens
  // a new session, every endTurn closes it.

  const armSilenceTimer = useCallback(() => {
    clearSilenceTimer();
    // 2026-06-26 Codex R41 — silence-auto-commit DISABLED.
    // Per the R41 voice redesign spec, final submission to AI must
    // be explicit (user taps 结束录音 / 发送). Silence detection
    // truncating mid-sentence ("但是。" then auto-submit) was the
    // top-reported voice bug. The timer body is now a noop but we
    // still arm it so the existing armSilenceTimer / clearSilenceTimer
    // call sites work unchanged; the only effect is the SILENCE_HOLD_MS
    // wait + a console log when it would have fired.
    silenceTimerRef.current = setTimeout(() => {
      silenceTimerRef.current = null;
      console.warn(
        '[voice-trace] silence timer fired — auto-commit DISABLED (R41); user must click 结束录音',
        {
          len: latestPartialRef.current.trim().length,
          phase: phaseRef.current,
        },
      );
    }, SILENCE_HOLD_MS);
  }, [clearSilenceTimer]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: mergeIntoTurnBuffer is a stable inner helper
  const onMessage = useCallback(
    (m: VoiceWsMessage) => {
      // 2026-06-26 Codex R41 — structured trace for every protocol event
      // (filters down to the things we want diagnosable: asr_started,
      // asr_ended, asr_error, plus partial/final text lengths). Ping/pong
      // skipped — too noisy and tells us nothing.
      if (m.type !== 'ping' && m.type !== 'pong') {
        const txt = typeof m.text === 'string' ? m.text : '';
        console.warn('[voice-trace] ws<-', {
          type: m.type,
          text_len: txt.length,
          phase: phaseRef.current,
          tts_hold: ttsHoldRef.current,
          ptt_pending: pttCommitPendingRef.current,
        });
      }
      // 2026-06-23 v3 — empty/whitespace-only finals come from Doubao
      // when the mic is muted (track.enabled=false) but PCM keeps
      // flowing. Its 800ms server endpoint detector fires definite=true
      // on the silence; the text is empty. Drop these unconditionally
      // before anything else looks at them — they're not user speech.
      if (m.type === 'asr_final' || m.type === 'asr_partial') {
        const text = typeof m.text === 'string' ? m.text.trim() : '';
        if (m.type === 'asr_final' && !text) {
          if (typeof console !== 'undefined') {
            console.warn('[voice v3] dropped empty asr_final (silence artifact)');
          }
          return;
        }
      }
      // CRO-108 (2026-06-17) — while TTS is playing, drop every ASR event.
      // 2026-06-23 v3 — with hardware-mic-mute the mic produces silence,
      // so partials should be empty or carry only AI-voice echo (via
      // bone-conduction / speaker leak). Drop them; do NOT latch
      // session-dead here — the reconnect dance was only needed when we
      // were tearing down between turns, and that path is gone in v3.
      if (ttsHoldRef.current && (m.type === 'asr_partial' || m.type === 'asr_final')) {
        return;
      }
      if (m.type === 'asr_partial') {
        if (phaseRef.current === 'off') return;
        // 2026-06-23 v4 — accept partials only while actively listening
        // (or thinking, for barge-in detection). When phase is 'idle'
        // (user paused) the PCM keeps flowing but we ignore the
        // transcripts; same when 'warmup' (initial handshake).
        if (phaseRef.current !== 'listening' && phaseRef.current !== 'thinking') {
          return;
        }
        const fullText = typeof m.text === 'string' ? m.text : '';
        // Track the longest cumulative session transcript we've ever
        // seen. Doubao SHOULD grow this monotonically; in practice it
        // doesn't always (see mergeIntoTurnBuffer). Storing the max
        // length keeps committedFullTextLen meaningful even after a
        // transient shrink.
        if (fullText.length > latestFullTextRef.current.length) {
          latestFullTextRef.current = fullText;
        }
        // V3.4 (2026-06-03 cross-utterance fix) — DO NOT mutate
        // committedFullTextLenRef on a transient shrink. The previous
        // code clamped committedFullTextLen DOWN to fullText.length
        // whenever Doubao briefly dropped the prefix (e.g. after a
        // mid-utterance silence). Once clamped, future partials with
        // the FULL session transcript (length > original committed
        // offset) sliced from the new tiny offset and surfaced the
        // already-committed prior turn's text inside the NEXT bubble.
        //
        // Symptom: turn #25 commits content X correctly. User pauses,
        // says Y. A phantom italic 'temp-econ-stream-*' bubble appears
        // with X+Y. Silence timer fires → turn #27 commits as X+Y.
        //
        // Fix: clamp the slice offset locally per-event without
        // touching the ref. The committed-length pointer ONLY moves
        // forward via fireFinal.
        const safeOffset = Math.min(committedFullTextLenRef.current, fullText.length);
        const rawDisplay = fullText.slice(safeOffset);
        // V3.4 — survive Doubao prefix loss / utterance restart.
        const display = mergeIntoTurnBuffer(rawDisplay);
        const trimmed = display.trim();

        // Barge-in: thinking + the current turn has new content =
        // user is talking over the AI. Abort LLM, back to listening.
        if (realtimeRef.current && phaseRef.current === 'thinking' && trimmed.length > 0) {
          if (abortRef.current) {
            abortRef.current.abort();
            abortRef.current = null;
          }
          setPhaseNow('listening');
          armIdleTimer();
        }

        const prevDisplay = latestPartialRef.current;
        latestPartialRef.current = display;
        onPartialRef.current(display);

        // 2026-06-24 v13 — PTT mode also arms silence timer (used to
        // auto-commit after 5s of no new partial). Removed the realtime
        // gate so silence-auto-commit works in unified PTT flow. The
        // silence-timer fire path internally branches on mode to either
        // (PTT) trigger stopRecording-then-asr_final-then-fireFinal or
        // (realtime) fireFinal with current partial.
        if (phaseRef.current === 'listening') {
          if (display !== prevDisplay) armSilenceTimer();
          if (realtimeRef.current) armIdleTimer();
        }
      } else if (m.type === 'asr_final') {
        // is_last (session closed) — server-side Doubao decided the
        // utterance is done. Originally we assumed this never fires in
        // realtime, but 2026-06-15 production observation: Doubao DOES
        // emit asr_final after long natural sentence pauses, AND closes
        // its session. Without restarting, every subsequent utterance
        // hits a dead session — only the first 1-3 chars before the
        // close survive, then silence.
        //
        // 2026-06-15 — handle realtime asr_final as: (a) preserve the
        // text into the bubble buffer (so onPartial sees the final
        // recognized form), (b) if manualMode is OFF, fire fireFinal
        // like before; if ON, skip the fire (user is in continue mode),
        // (c) UNCONDITIONALLY restart the Doubao session so the next
        // utterance has a live ASR endpoint to talk to. The restart is
        // the same dance asr_error uses.
        const t = typeof m.text === 'string' ? m.text.trim() : '';
        if (!t) return;
        if (phaseRef.current === 'thinking') return;
        // 2026-06-23 v4 — also drop finals during idle/warmup/off.
        // PCM keeps flowing for session-keepalive but we don't want
        // an asr_final captured during a pause to pop the confirm
        // card or move the committed pointer.
        if (phaseRef.current !== 'listening') return;
        if (realtimeRef.current) {
          clearSilenceTimer();
          if (manualModeRef.current) {
            // Manual mode: don't pop the confirm card. Keep the text in
            // the per-turn buffer so commitNow can use it later.
            turnDisplayBufferRef.current = t;
            latestPartialRef.current = t;
            onPartialRef.current(t);
          } else {
            latestPartialRef.current = '';
            fireFinalRef.current(t);
          }
          // 2026-06-23 v4 — DO NOT reconnect on asr_final in realtime.
          // In the always-hot architecture, asr_final usually means
          // Doubao decided this is the end of a definite utterance; we
          // just committed it via fireFinal. The session may or may not
          // have closed (we never send is_last from the client). If it
          // closed, hermes will surface an asr_error on the NEXT user
          // audio chunk, which falls through to the asr_error reconnect
          // path below. Reconnecting eagerly here was the source of the
          // "asr_started ack timeout" error: it tore down a session
          // that might still be alive, and re-opening competed with
          // the next utterance's startup window.
        } else {
          if (pttCommitPendingRef.current) {
            pttCommitPendingRef.current = false;
            // 2026-06-23 v7 — clear watchdog: asr_final landed cleanly.
            if (endTurnWatchdogRef.current) {
              clearTimeout(endTurnWatchdogRef.current);
              endTurnWatchdogRef.current = null;
            }
            // V3.4 (2026-06-03 audit V5) — PTT path used to slice
            // `t.slice(committedFullTextLenRef.current)`. PTT opens a
            // FRESH Doubao session per turn (startTurn / endTurn pair),
            // so committedFullTextLen from turn N is stale on turn N+1
            // and chopped off the front of the second utterance. The
            // session-cumulative slice contract only holds for the
            // realtime branch where the session is long-lived.
            //
            // Fix: PTT commit uses the full text. Also reset the
            // committed length so realtime ↔ PTT toggles start clean.
            fireFinalRef.current(t);
            committedFullTextLenRef.current = 0;
          } else {
            // 2026-06-25 v25.2 — Doubao bigmodel decided this is a
            // complete utterance (1s pause + content semantics) and
            // closed its session WITHOUT us asking. In the v4-v24
            // always-hot era we tried to "recover" via stopRecording +
            // 80ms sleep + startRecording to keep the user talking on
            // a new session. With per-turn architecture (v25) that's
            // both unnecessary and harmful: the async reopen takes
            // ~5-6s and collides with the user's 继续讲 click which
            // also calls startTurn → competing startRecording calls →
            // mic stuck.
            //
            // New behavior: treat Doubao's autonomous close as the
            // user's utterance ending. Fire final → confirm card pops
            // → user can click 继续讲 to add more (cleanly opens new
            // Doubao via per-turn cold path) or 发送 to commit.
            //
            // This aligns with the bigmodel's own semantic intent:
            // it thinks the user is done. If user wasn't actually
            // done, 继续讲 + per-turn architecture handles it
            // reliably.
            if (phaseRef.current === 'listening') {
              if (typeof console !== 'undefined') {
                console.warn('[voice v25.2] mid-PTT Doubao close — firing as final', {
                  text_chars: t.length,
                });
              }
              fireFinalRef.current(t);
              committedFullTextLenRef.current = 0;
              return;
            }
            onPartialRef.current(t);
          }
        }
      } else if (m.type === 'asr_error') {
        const rawMsg = typeof m.message === 'string' ? m.message : 'asr error';
        // 2026-06-23 v7 — translate Doubao internal error text into a
        // single user-facing Chinese line. Round 19 Codex caught the raw
        // Doubao string ("[Timeout waiting next packet] waiting next
        // packet timeout: 8.000000 seconds, session has ended") leaking
        // into the UI banner. Anything Doubao-internal becomes the same
        // friendly message; only genuine novel errors fall through.
        const friendly = translateAsrError(rawMsg);
        // V3.4 (2026-06-03 audit V4) — unconditionally abort any
        // in-flight LLM stream before the reconnect or idle drop.
        if (abortRef.current) {
          abortRef.current.abort();
          abortRef.current = null;
        }
        if (
          realtimeRef.current &&
          (phaseRef.current === 'listening' ||
            phaseRef.current === 'warmup' ||
            phaseRef.current === 'thinking')
        ) {
          // Surface 'warmup' during reconnect so the UI hint matches
          // reality (mic is being reopened; user shouldn't speak yet).
          setPhaseNow('warmup');
          void (async () => {
            try {
              await voice.stopRecording();
              voice.disconnect();
              await new Promise((r) => setTimeout(r, 300));
              await voice.connect();
              await voice.startRecording(realtimeRef.current ? 'realtime' : 'ptt');
              // Fresh Doubao session — restart turn bookkeeping.
              resetTurnPointers();
              setPhaseNow('listening');
            } catch (e) {
              const base = friendly ?? '语音连接出错';
              const detail = e instanceof Error ? `${base}（重连失败：${e.message}）` : base;
              onErrorRef.current?.(detail);
              setPhaseNow('off');
              onFatalErrorRef.current?.(detail);
            }
          })();
          return;
        }
        // 2026-06-23 v7 — PTT (单次) recovery. If the user was waiting
        // for the asr_final after 结束发送, asr_error means it'll never
        // come; clear pttCommitPending + watchdog so the next 点击说话
        // starts a fresh turn. Then drop to idle. The friendly message
        // goes to the banner — never to the transcript bubble.
        pttCommitPendingRef.current = false;
        if (endTurnWatchdogRef.current) {
          clearTimeout(endTurnWatchdogRef.current);
          endTurnWatchdogRef.current = null;
        }
        // Suppress transient Doubao auto-recovery noise (hermes reopens
        // the session on its own; user doesn't need to see it).
        if (friendly) onErrorRef.current?.(friendly);
        setPhaseNow(phaseRef.current === 'off' ? 'off' : 'idle');
      }
    },
    [armSilenceTimer, armIdleTimer, clearSilenceTimer, resetTurnPointers, setPhaseNow],
  );

  const voice = useVoiceWs({ autoConnect: false, onMessage });

  const fireFinal = useCallback(
    (t: string) => {
      setPhaseNow('thinking');
      clearSilenceTimer();
      clearIdleTimer();
      // 2026-06-23 — every commit (= "send") resets the 3-min cap.
      // Was per-session (3 min total record budget); now per-utterance
      // (3 min between sends). Matches user expectation: "我发送了一段
      // 之后，可以把3分钟的倒计时重新计时么？"
      startSessionTimer();

      // Mark the turn boundary at the current Doubao transcript length.
      // Future partials slice from this offset to display the NEXT turn.
      // Mic + Doubao stay running — no audio_end / audio_start dance.
      committedFullTextLenRef.current = latestFullTextRef.current.length;
      latestPartialRef.current = '';
      // V3.4 — reset the per-turn merge buffer so the next turn starts fresh.
      turnDisplayBufferRef.current = '';

      abortRef.current?.abort();
      const ac = new AbortController();
      abortRef.current = ac;

      void (async () => {
        const t0 = typeof performance !== 'undefined' ? performance.now() : Date.now();
        let llmError: unknown;
        try {
          await onFinalRef.current(t, ac.signal);
          if (typeof console !== 'undefined') {
            const t1 = typeof performance !== 'undefined' ? performance.now() : Date.now();
          }
        } catch (e) {
          if (ac.signal.aborted) return; // barge-in already flipped phase
          llmError = e;
        }
        if (abortRef.current === ac) abortRef.current = null;
        if (phaseRef.current !== 'thinking') return;

        if (realtimeRef.current) {
          // Mic + Doubao were never paused — listening is instant.
          setPhaseNow('listening');
          armSilenceTimer();
          armIdleTimer();
        } else {
          setPhaseNow('idle');
        }

        if (llmError) {
          onErrorRef.current?.(llmError instanceof Error ? llmError.message : String(llmError));
        }
      })();
    },
    [
      armSilenceTimer,
      armIdleTimer,
      clearSilenceTimer,
      clearIdleTimer,
      setPhaseNow,
      startSessionTimer,
    ],
  );
  useEffect(() => {
    fireFinalRef.current = fireFinal;
  }, [fireFinal]);

  // 2026-06-23 v4 — idle = application-layer filter only. PCM keeps
  // flowing to keep the Doubao session alive (see holdForTts comment
  // for why setMicEnabled(false) is too aggressive). onMessage's
  // phase-guard already prevents idle-state partials from showing in
  // the UI, so nothing reaches the bubble.

  // 2026-06-15 — CF Workers WebSocket idle ~100s gets cut even with hermes
  // ping/pong; the heartbeat watchdog in use-voice-ws.ts force-closes with
  // code 4001 ('heartbeat_timeout'), but until now nothing in chat-loop
  // listened for the close. Result: voice.status flips to 'closed' but
  // chat-loop's phase stays 'listening', UI shows 「实时聆听」, mic is dead.
  // Watch voice.status flips while we're supposed to be listening and
  // trigger the same reconnect path that asr_error uses.
  // 2026-06-16 #3 — track reconnect attempts so the user isn't bounced
  // back to text mode after a single 1006 hiccup (CF Workers idle close
  // is common at 30-60s). Reset on any clean ws:open. After 3 failed
  // reconnects we give up — the user can re-click 「切换到语音」.
  const reconnectAttemptsRef = useRef(0);
  useEffect(() => {
    if (voice.status === 'open') {
      reconnectAttemptsRef.current = 0;
    }
  }, [voice.status]);
  // biome-ignore lint/correctness/useExhaustiveDependencies: voice is stable
  useEffect(() => {
    if (voice.status !== 'closed' && voice.status !== 'error') return;
    if (phaseRef.current === 'off') return;
    // 2026-06-23 v7 — 单次 (PTT) recovery on WS close. Previously this
    // effect early-returned for !realtime, leaving phase stuck at
    // listening with a dead mic + dead WS — Codex round 19 saw 结束发送
    // frozen with no way back to 点击说话. In PTT we don't auto-reconnect
    // (user already saw their input drop), but we DO reset phase to idle
    // and clear pttCommitPending so the next 点击说话 starts clean.
    if (!realtimeRef.current) {
      if (
        phaseRef.current === 'listening' ||
        phaseRef.current === 'warmup' ||
        phaseRef.current === 'thinking'
      ) {
        pttCommitPendingRef.current = false;
        if (abortRef.current) {
          abortRef.current.abort();
          abortRef.current = null;
        }
        clearSilenceTimer();
        clearIdleTimer();
        // voice.recording flips to false naturally when the worklet
        // and audio context tear down; we just reflect that in phase.
        setPhaseNow('idle');
      }
      return;
    }
    if (
      phaseRef.current !== 'listening' &&
      phaseRef.current !== 'warmup' &&
      phaseRef.current !== 'thinking'
    ) {
      return;
    }
    reconnectAttemptsRef.current++;
    const attempt = reconnectAttemptsRef.current;
    const MAX_RECONNECT = 3;
    if (attempt > MAX_RECONNECT) {
      console.error(`[VOICE] ws ${voice.status} — giving up after ${MAX_RECONNECT} retries`);
      const msg = `语音连接持续断开 (${voice.status})，已重试 ${MAX_RECONNECT} 次。点「切换到语音」可手动重连。`;
      onErrorRef.current?.(msg);
      setPhaseNow('off');
      onFatalErrorRef.current?.(msg);
      return;
    }
    // Exponential backoff: 300ms, 900ms, 2700ms. Keeps quick-recoverable
    // network blips snappy while not hammering hermes if the backend is
    // actually down.
    const backoffMs = 300 * 3 ** (attempt - 1);
    console.warn(
      `[VOICE] ws status ${voice.status} — auto-reconnect attempt ${attempt}/${MAX_RECONNECT} after ${backoffMs}ms`,
    );
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    setPhaseNow('warmup');
    void (async () => {
      try {
        await voice.stopRecording().catch(() => {});
        voice.disconnect();
        await new Promise((r) => setTimeout(r, backoffMs));
        await voice.connect();
        await voice.startRecording(realtimeRef.current ? 'realtime' : 'ptt');
        resetTurnPointers();
        setPhaseNow('listening');
      } catch (e) {
        // Don't drop to 'off' yet — the next status change will re-enter
        // this effect and either retry (attempt < MAX) or hard-fail.
        const msg = e instanceof Error ? e.message : 'voice reconnect failed';
        console.warn(`[VOICE] reconnect attempt ${attempt} failed: ${msg}`);
      }
    })();
  }, [voice.status, resetTurnPointers, setPhaseNow, clearSilenceTimer, clearIdleTimer]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: voice is stable
  const enterVoice = useCallback(async (): Promise<boolean> => {
    if (phaseRef.current !== 'off') return true;
    try {
      await voice.connect();
      if (realtimeRef.current) {
        // V3.4 (2026-06-03) — show 'warmup' (「正在准备麦克风…」) until
        // hermes ack-s asr_started. startRecording awaits the ack so
        // we flip to 'listening' once it returns.
        setPhaseNow('warmup');
        await voice.startRecording(realtimeRef.current ? 'realtime' : 'ptt');
        // Fresh Doubao session — reset turn bookkeeping AFTER
        // startRecording succeeds.
        resetTurnPointers();
        setPhaseNow('listening');
        armIdleTimer();
      } else {
        setPhaseNow('idle');
        resetTurnPointers();
      }
      // 2026-06-23 — kick off the 3-min session cap.
      startSessionTimer();
      return true;
    } catch (e) {
      onErrorRef.current?.(e instanceof Error ? e.message : 'voice start failed');
      setPhaseNow('off');
      return false;
    }
  }, [armIdleTimer, resetTurnPointers, setPhaseNow, startSessionTimer]);

  // 2026-06-25 v25 Phase 3 — visibility API: tab hidden 5s → tear
  // down WS so we don't keep Hermes/Doubao slots warm for a user
  // who walked away. Reconnect happens automatically on next user
  // action (startTurn → voice.connect() is idempotent).
  // biome-ignore lint/correctness/useExhaustiveDependencies: voice is stable
  useEffect(() => {
    if (typeof document === 'undefined') return;
    let hideTimer: ReturnType<typeof setTimeout> | null = null;
    function onVisibilityChange() {
      if (document.hidden) {
        if (hideTimer) clearTimeout(hideTimer);
        hideTimer = setTimeout(() => {
          hideTimer = null;
          if (!document.hidden) return;
          if (phaseRef.current === 'off') return;
          console.warn('[voice-trace] tab hidden 5s — disconnecting WS to release slot');
          // Soft tear-down: stop recording + disconnect WS, but leave
          // the React state intact so when user comes back the voice
          // panel still shows their session context. Next startTurn
          // re-runs voice.connect() which is idempotent.
          void voice.stopRecording().catch(() => {});
          voice.disconnect();
          setPhaseNow('idle');
        }, 5000);
      } else {
        if (hideTimer) {
          clearTimeout(hideTimer);
          hideTimer = null;
        }
      }
    }
    document.addEventListener('visibilitychange', onVisibilityChange);
    return () => {
      if (hideTimer) clearTimeout(hideTimer);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [setPhaseNow]);

  // biome-ignore lint/correctness/useExhaustiveDependencies: voice is stable
  const exitVoice = useCallback(() => {
    if (phaseRef.current === 'off') return;
    pttCommitPendingRef.current = false;
    clearEndTurnWatchdog();
    clearSilenceTimer();
    clearIdleTimer();
    clearSessionTimer();
    latestPartialRef.current = '';
    abortRef.current?.abort();
    abortRef.current = null;
    void voice.stopRecording().catch(() => {});
    voice.disconnect();
    setPhaseNow('off');
  }, [clearSilenceTimer, clearIdleTimer, clearSessionTimer, clearEndTurnWatchdog, setPhaseNow]);
  // 2026-06-23 — register exitVoice as the session-expire handler so
  // the timer can call it without forward-reference. Re-binds whenever
  // exitVoice changes (deps above).
  useEffect(() => {
    onSessionExpireRef.current = exitVoice;
    return () => {
      onSessionExpireRef.current = null;
    };
  }, [exitVoice]);

  // 2026-06-25 v25 — Option B: per-turn Doubao session. Always go
  // through the cold path (`voice.startRecording`) which sends
  // `audio_start` to Hermes → opens fresh Doubao WS session → waits
  // `asr_started` ack → unlocks PCM stream.
  //
  // Why drop the v4 "always-hot" shortcut: it was an attempted
  // optimization to skip ~300ms of Doubao handshake per turn by
  // keeping one Doubao session open for the whole voice mode. In
  // practice it caused turn-N-to-turn-N+1 state leaks that v18-v24
  // chased with defensive resets — and Leon kept reporting "1st
  // attempt fails, 2nd works, 3rd fails again" because the shortcut
  // returned 'listening' while the underlying Doubao session was
  // half-dead (post asr_error reopen, post WS drop, or just
  // hallucinating "my name is" tail from a previous session's audio
  // tail). Per-turn architecture has zero cross-turn state.
  //
  // Cost: ~300-500ms extra warmup per turn. UX still better since
  // the previous "instant" was actually 5-30% failure. The warmup
  // happens during the window where the user is just tapping
  // 点击说话 and hasn't started speaking — invisible.
  //
  // Hermes side already supports per-turn (audio_start opens Doubao,
  // audio_end closes Doubao). No protocol change needed.
  // biome-ignore lint/correctness/useExhaustiveDependencies: voice is stable
  const startTurn = useCallback(async (): Promise<void> => {
    if (phaseRef.current !== 'idle') {
      // 2026-06-26 Codex R41 — when 点击说话 silently does nothing,
      // this trace tells us the phase we got stuck in (probably
      // 'thinking' or 'aiSpeaking' that never reset to idle after
      // the previous turn).
      console.warn('[voice-trace] startTurn BLOCKED — phase not idle', {
        phase: phaseRef.current,
        voice_recording: voice.recording,
        ws_status: voice.status,
        tts_hold: ttsHoldRef.current,
      });
      return;
    }
    // v24-era defensive reset retained — cheap insurance even with
    // per-turn architecture (refs zeroed before any await).
    clearSilenceTimer();
    clearEndTurnWatchdog();
    pttCommitPendingRef.current = false;
    ttsHoldRef.current = false;
    resetTurnPointers();

    console.warn('[voice-trace] startTurn enter (v25 cold path)', {
      phase: phaseRef.current,
      voice_recording: voice.recording,
      ws_status: voice.status,
      realtime: realtimeRef.current,
      manual: manualModeRef.current,
    });

    try {
      // v25 — ALWAYS cold path. No shortcut. startRecording is fully
      // self-cleaning: it tears down any leftover worklet/stream
      // (use-voice-ws.ts lines 459-467), then opens a fresh mic +
      // sends audio_start to Hermes → fresh Doubao session.
      //
      // v25.1 — do NOT call stopRecording in an edge case here.
      // If voice.recording is stale-true but Hermes' asr is None
      // (no Doubao session), audio_end is silently dropped on the
      // Hermes side (main.py:1362 `if asr is not None` short-circuit)
      // → no asr_ended ack ever fires → client hangs 8s on
      // waitForAck('ended') → user sees "语音连接超时" banner.
      // startRecording's internal cleanup is sufficient.
      setPhaseNow('warmup');
      await voice.connect();
      await voice.startRecording(realtimeRef.current ? 'realtime' : 'ptt');
      setPhaseNow('listening');
      if (realtimeRef.current) armIdleTimer();
    } catch (e) {
      setPhaseNow('idle');
      onErrorRef.current?.(e instanceof Error ? e.message : 'mic start failed');
    }
  }, [armIdleTimer, clearEndTurnWatchdog, clearSilenceTimer, resetTurnPointers, setPhaseNow]);

  // PTT: manually end + commit.
  // biome-ignore lint/correctness/useExhaustiveDependencies: voice is stable
  const endTurn = useCallback(async (): Promise<void> => {
    if (phaseRef.current !== 'listening') return;
    pttCommitPendingRef.current = true;
    clearEndTurnWatchdog();
    endTurnWatchdogRef.current = setTimeout(() => {
      endTurnWatchdogRef.current = null;
      if (!pttCommitPendingRef.current) return;
      pttCommitPendingRef.current = false;
      console.warn('[voice v7] endTurn watchdog fired — no asr_final, resetting to idle');
      if (phaseRef.current === 'listening' || phaseRef.current === 'thinking') {
        if (abortRef.current) {
          abortRef.current.abort();
          abortRef.current = null;
        }
        setPhaseNow('idle');
        onErrorRef.current?.('没收到识别结果，请重新说一次。');
      }
    }, 4000);
    await voice.stopRecording().catch(() => {});
  }, [clearEndTurnWatchdog, setPhaseNow]);

  // 2026-06-15 — 「讲完了」 explicit-commit button. Realtime listening
  // normally waits SILENCE_HOLD_MS (5s) of no new partial before
  // firing the final. This lets the user say "I'm done" immediately
  // without waiting. We just run the same fireFinal path the silence
  // timer would, using whatever's currently in the per-turn buffer.
  const commitNow = useCallback(() => {
    if (phaseRef.current !== 'listening') return;
    const t = (turnDisplayBufferRef.current || latestPartialRef.current).trim();
    if (!t) return;
    fireFinalRef.current(t);
  }, []);

  // 2026-06-23 v3 — toggle 单次 ↔ 实时. No more stop/start cycles;
  // the mic is just muted via setMicEnabled.
  // biome-ignore lint/correctness/useExhaustiveDependencies: voice is stable across the WS session lifetime
  const setRealtimeAndArm = useCallback(
    async (next: boolean): Promise<void> => {
      setRealtime(next);
      realtimeRef.current = next;
      if (!next) {
        clearSilenceTimer();
        clearIdleTimer();
        if (phaseRef.current === 'listening' || phaseRef.current === 'thinking') {
          abortRef.current?.abort();
          abortRef.current = null;
          setPhaseNow('idle');
        }
        return;
      }
      if (phaseRef.current === 'idle') {
        if (voice.recording) {
          resetTurnPointers();
          setPhaseNow('listening');
          armIdleTimer();
          return;
        }
        // Cold path — stream gone, full handshake.
        setPhaseNow('warmup');
        try {
          await voice.startRecording(realtimeRef.current ? 'realtime' : 'ptt');
          resetTurnPointers();
          setPhaseNow('listening');
          armIdleTimer();
        } catch (e) {
          onErrorRef.current?.(e instanceof Error ? e.message : 'mic start failed');
          setPhaseNow('idle');
        }
      }
    },
    [armIdleTimer, clearSilenceTimer, clearIdleTimer, resetTurnPointers, setPhaseNow],
  );

  // 2026-06-23 v6 — armAfterTurn = "return mic to whatever mode the
  // user actually selected, after an LLM turn closes". Replaces the
  // old hardcoded setRealtimeAndArm(true) in handleConfirmSend, which
  // silently forced 单次 users back into 实时 every time they submitted.
  // In 单次, just go idle (user must click 点击说话 again). In 实时,
  // arm listening so the next utterance flows automatically.
  // biome-ignore lint/correctness/useExhaustiveDependencies: voice is stable
  const armAfterTurn = useCallback(async (): Promise<void> => {
    if (phaseRef.current === 'off') return;
    if (!realtimeRef.current) {
      clearSilenceTimer();
      clearIdleTimer();
      setPhaseNow('idle');
      return;
    }
    if (voice.recording) {
      resetTurnPointers();
      setPhaseNow('listening');
      armIdleTimer();
      return;
    }
    setPhaseNow('warmup');
    try {
      await voice.startRecording(realtimeRef.current ? 'realtime' : 'ptt');
      resetTurnPointers();
      setPhaseNow('listening');
      armIdleTimer();
    } catch (e) {
      onErrorRef.current?.(e instanceof Error ? e.message : 'mic start failed');
      setPhaseNow('idle');
    }
  }, [armIdleTimer, clearSilenceTimer, clearIdleTimer, resetTurnPointers, setPhaseNow]);

  // 2026-06-23 v3 — pauseListening just flips phase to idle. The
  // phase==='idle' useEffect mutes the mic. No teardown.
  const pauseListening = useCallback(() => {
    if (phaseRef.current !== 'listening' && phaseRef.current !== 'thinking') return;
    clearSilenceTimer();
    clearIdleTimer();
    setPhaseNow('idle');
  }, [clearSilenceTimer, clearIdleTimer, setPhaseNow]);

  const setManualMode = useCallback(
    (on: boolean) => {
      manualModeRef.current = on;
      if (on) {
        // Cancel any pending silence timer so it doesn't fire after we
        // flip into manual.
        clearSilenceTimer();
      }
    },
    [clearSilenceTimer],
  );

  /**
   * CRO-108 (2026-06-17) — pause mic + transcript capture while the AI's
   * TTS plays so the mic doesn't pick up the speaker output and feed
   * garbled ASR back into the next turn. Without this gate Amy saw the
   * 「确认你刚说的」 card pop with TTS-misheard text like 「握时应该会」
   * AND the silence timer would fire mid-AI-question (= "AI 抢答").
   *
   * holdForTts: phase → 'warmup' (UI shows 「准备中」), stop the mic,
   *   suppress partials/finals via a ref the onMessage handler checks.
   * resumeFromTts: start the mic again, reset turn pointers so any
   *   tail-end ASR echo from the prior turn doesn't leak in, phase →
   *   'listening' with a fresh silence timer.
   */
  // 2026-06-23 v4 — TTS hold = application-layer filter ONLY. The mic
  // and Doubao session both stay hot, PCM never stops flowing.
  //
  // Why not setMicEnabled(false)? Empirically, audioTrack.enabled=false
  // causes the AudioWorklet to stop pumping (Chrome's MediaStreamSource
  // stops producing samples), and Doubao times out after 8s of no
  // packets with "[Timeout waiting next packet] session has ended".
  // That kills the session and the next user utterance never reaches
  // ASR until a full reconnect.
  //
  // Trade-off: while TTS plays, Doubao IS transcribing whatever the
  // mic picks up (browser echo-cancellation + AGC + noiseSuppression
  // are on, so speaker leak should be minimal). Any partials / finals
  // arriving while ttsHoldRef is true get dropped in onMessage. Cost:
  // a few extra cents of Doubao usage; benefit: session never dies.
  const holdForTts = useCallback(() => {
    if (phaseRef.current === 'off') return;
    ttsHoldRef.current = true;
    clearSilenceTimer();
    clearIdleTimer();
  }, [clearSilenceTimer, clearIdleTimer]);
  const resumeFromTts = useCallback(() => {
    if (!ttsHoldRef.current) return;
    ttsHoldRef.current = false;
    if (phaseRef.current === 'off') return;
    resetTurnPointers();
    // 2026-06-23 v6 — respect 单次 vs 实时. In 单次 mode the user
    // explicitly clicks 点击说话 per utterance, so we MUST NOT auto-arm
    // listening after TTS ends — that left the UI in "listening" on a
    // session that the user thought was paused, which is exactly the
    // "AI回复后再说话没有收到我的输入" symptom Leon reported.
    if (!realtimeRef.current) {
      setPhaseNow('idle');
      return;
    }
    setPhaseNow('listening');
    armSilenceTimer();
    armIdleTimer();
  }, [armSilenceTimer, armIdleTimer, resetTurnPointers, setPhaseNow]);

  // 2026-06-23 — `restartSession` is the "继续" button after a 3-min
  // expiry. Does a full tear-down (in case the prior session is still
  // half-alive) followed by a fresh enterVoice. session-panel calls
  // this when it sees sessionExpired === true and the user clicks
  // 「继续」. Idempotent — safe to call multiple times.
  const restartSession = useCallback(async (): Promise<boolean> => {
    exitVoice();
    // Tiny gap so any in-flight teardown completes cleanly before
    // we open a new WS + mic.
    await new Promise((r) => setTimeout(r, 50));
    return enterVoice();
  }, [enterVoice, exitVoice]);

  // 2026-06-26 (Codex R41 ask) — user-facing 「重置语音」 escape hatch.
  // When the state machine wedges (no audio reaches Doubao, watchdog
  // hasn't fired yet, click-end does nothing), the user shouldn't have
  // to wait 8s for the timeout or refresh the page. This nukes
  // everything synchronously: stop mic, disconnect WS, clear all
  // timers + refs, return to idle. Next 点击说话 path is a true cold
  // start.
  // biome-ignore lint/correctness/useExhaustiveDependencies: voice is stable
  const resetVoice = useCallback(async (): Promise<void> => {
    console.warn('[voice-trace] resetVoice — user-triggered hard reset', {
      phase: phaseRef.current,
      voice_recording: voice.recording,
      ws_status: voice.status,
    });
    pttCommitPendingRef.current = false;
    ttsHoldRef.current = false;
    clearSilenceTimer();
    clearEndTurnWatchdog();
    clearIdleTimer();
    clearSessionTimer();
    latestPartialRef.current = '';
    resetTurnPointers();
    if (abortRef.current) {
      abortRef.current.abort();
      abortRef.current = null;
    }
    try {
      await voice.stopRecording();
    } catch {
      // best-effort; tear-down errors are fine here
    }
    voice.disconnect();
    setPhaseNow('idle');
    // Tiny gap so any teardown side effect (audio worklet stop) drains.
    await new Promise((r) => setTimeout(r, 80));
    // Re-open the WS so the next startTurn doesn't pay the WS handshake
    // cost (~200ms). Mic stays off until startTurn fires audio_start.
    try {
      await voice.connect();
    } catch (e) {
      console.warn('[voice-trace] resetVoice — reconnect after disconnect failed', e);
    }
  }, [
    clearEndTurnWatchdog,
    clearIdleTimer,
    clearSessionTimer,
    clearSilenceTimer,
    resetTurnPointers,
    setPhaseNow,
  ]);

  return {
    phase,
    realtime,
    setRealtime,
    setRealtimeAndArm,
    enterVoice,
    exitVoice,
    startTurn,
    endTurn,
    pauseListening,
    commitNow,
    armAfterTurn,
    setManualMode,
    holdForTts,
    resumeFromTts,
    ttsHoldRef,
    status: voice.status,
    // 2026-06-23 — session countdown surface.
    remainingSec,
    sessionExpired,
    restartSession,
    // 2026-06-26 Codex R41 — hard reset escape hatch.
    resetVoice,
  };
}
